export type MarkdownEmphasisVariant = "italic" | "bold" | "boldItalic";

export type MarkdownTextRun =
  | { type: "text"; text: string }
  | { type: "inlineCode"; code: string }
  | { type: "link"; text: string; href: string; external: boolean }
  | { type: "emphasis"; variant: MarkdownEmphasisVariant; children: MarkdownTextRun[] };

export type MarkdownListItem = {
  content: MarkdownTextRun[];
};

export type MarkdownTableAlignment = "left" | "center" | "right" | "none";

export type MarkdownTableCell = {
  content: MarkdownTextRun[];
};

export type MarkdownTableRow = {
  cells: MarkdownTableCell[];
};

type MarkdownHeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export type MarkdownBlock =
  | { type: "heading"; level: MarkdownHeadingLevel; id: string; content: MarkdownTextRun[] }
  | { type: "paragraph"; content: MarkdownTextRun[] }
  | { type: "bulletList"; items: MarkdownListItem[] }
  | { type: "numberList"; start: number; items: MarkdownListItem[] }
  | { type: "code"; language?: string; content: string }
  | { type: "blockquote"; content: MarkdownTextRun[] }
  | { type: "table"; header: MarkdownTableRow; rows: MarkdownTableRow[]; alignments: MarkdownTableAlignment[] };

export type MarkdownHeading = {
  id: string;
  depth: MarkdownHeadingLevel;
  text: string;
};

export type MarkdownDocument = {
  blocks: MarkdownBlock[];
  headings: MarkdownHeading[];
  source: string;
};

export type MarkdownWarningCode = "UNSUPPORTED_IMAGE" | "TASK_LIST" | "MALFORMED_LINK";

export type MarkdownParseWarning = {
  code: MarkdownWarningCode;
  message: string;
  line: number;
};

type ListState = {
  kind: "bullet" | "number";
  start: number;
  items: { text: string; line: number }[];
};

type CodeState = {
  fence: string;
  language?: string;
  lines: string[];
};

const WARNING_MESSAGES: Record<MarkdownWarningCode, string> = {
  UNSUPPORTED_IMAGE: "Images are not rendered inside markdown content.",
  TASK_LIST: "Task list syntax renders as plain text.",
  MALFORMED_LINK: "Link syntax is malformed; showing literal text.",
};

export function parseMarkdownDocument(source?: string | null): {
  document: MarkdownDocument;
  warnings: MarkdownParseWarning[];
} {
  const normalized = (source ?? "").replace(/\r\n?/g, "\n");
  if (!normalized.trim()) {
    return { document: { blocks: [], headings: [], source: "" }, warnings: [] };
  }

  const lines = normalized.split("\n");
  const blocks: MarkdownBlock[] = [];
  const warnings: MarkdownParseWarning[] = [];
  const headings: MarkdownHeading[] = [];
  const slugCounts = new Map<string, number>();

  let paragraphBuffer: { text: string; line: number }[] = [];
  let blockquoteBuffer: { text: string; line: number }[] = [];
  let listState: ListState | null = null;
  let codeState: CodeState | null = null;

  const addWarning = (code: MarkdownWarningCode, line: number) => {
    warnings.push({ code, line, message: WARNING_MESSAGES[code] });
  };

  const flushParagraph = () => {
    if (!paragraphBuffer.length) return;
    const text = paragraphBuffer.map((entry) => entry.text).join(" ");
    const line = paragraphBuffer[0].line;
    blocks.push({ type: "paragraph", content: parseInline(text, line, warnings) });
    paragraphBuffer = [];
  };

  const flushBlockquote = () => {
    if (!blockquoteBuffer.length) return;
    const text = blockquoteBuffer.map((entry) => entry.text).join(" ");
    const line = blockquoteBuffer[0].line;
    blocks.push({ type: "blockquote", content: parseInline(text, line, warnings) });
    blockquoteBuffer = [];
  };

  const flushList = () => {
    if (!listState) return;
    const items: MarkdownListItem[] = listState.items.map((item) => ({
      content: parseInline(item.text, item.line, warnings),
    }));
    if (listState.kind === "bullet") {
      blocks.push({ type: "bulletList", items });
    } else {
      blocks.push({ type: "numberList", start: listState.start, items });
    }
    listState = null;
  };

  const flushCode = () => {
    if (!codeState) return;
    blocks.push({ type: "code", language: codeState.language, content: codeState.lines.join("\n") });
    codeState = null;
  };

  for (let index = 0; index < lines.length; index += 1) {
    const rawLine = lines[index];
    const lineNumber = index + 1;
    const line = rawLine.replace(/\s+$/u, "");
    const trimmed = line.trim();

    if (codeState) {
      if (trimmed.startsWith(codeState.fence)) {
        flushCode();
        continue;
      }
      codeState.lines.push(rawLine);
      continue;
    }

    if (!trimmed) {
      flushParagraph();
      flushList();
      flushBlockquote();
      continue;
    }

    const tableHeaderCells = tryParseTableRow(trimmed);
    if (tableHeaderCells) {
      const dividerCells = tryParseTableDivider(lines[index + 1], tableHeaderCells.length);
      if (dividerCells) {
        flushParagraph();
        flushList();
        flushBlockquote();

        const columnCount = tableHeaderCells.length;
        const headerRow = buildTableRow(tableHeaderCells, columnCount, lineNumber, warnings);
        const alignments = parseTableAlignments(dividerCells, columnCount);
        const rows: MarkdownTableRow[] = [];

        let bodyIndex = index + 2;
        while (bodyIndex < lines.length) {
          const bodyLine = lines[bodyIndex];
          const bodyTrimmed = bodyLine.trim();
          if (!bodyTrimmed) {
            break;
          }
          const bodyCells = tryParseTableRow(bodyTrimmed);
          if (!bodyCells) {
            break;
          }
          rows.push(buildTableRow(bodyCells, columnCount, bodyIndex + 1, warnings));
          bodyIndex += 1;
        }

        blocks.push({ type: "table", header: headerRow, rows, alignments });
        index = bodyIndex - 1;
        continue;
      }
    }

    if (/!\[[^\]]*\]\([^)]*\)/u.test(trimmed)) {
      addWarning("UNSUPPORTED_IMAGE", lineNumber);
    }

    if (trimmed.startsWith("```")) {
      flushParagraph();
      flushList();
      flushBlockquote();
      const fence = trimmed.match(/^`+/u)?.[0] ?? "```";
      const language = trimmed.slice(fence.length).trim() || undefined;
      codeState = { fence, language, lines: [] };
      continue;
    }

    if (trimmed.startsWith(">") || trimmed.startsWith("> ")) {
      flushParagraph();
      flushList();
      const quoteText = trimmed.replace(/^>\s?/u, "");
      blockquoteBuffer.push({ text: quoteText, line: lineNumber });
      continue;
    }

    const headingMatch = /^(#{1,6})\s+(.*)$/u.exec(trimmed);
    if (headingMatch) {
      const level = headingMatch[1].length as MarkdownHeadingLevel;

      flushParagraph();
      flushList();
      flushBlockquote();

      const headingText = headingMatch[2].trim();
      const inlineContent = parseInline(headingText, lineNumber, warnings);
      const plainHeading = toPlainText(inlineContent);
      const id = slugify(plainHeading || "heading", slugCounts);

      headings.push({ id, depth: level, text: plainHeading });
      blocks.push({ type: "heading", level, id, content: inlineContent });
      continue;
    }

    const numberListMatch = /^(\d+)\.\s+(.*)$/u.exec(trimmed);
    if (numberListMatch) {
      flushParagraph();
      flushBlockquote();
      const value = numberListMatch[2];
      if (!listState || listState.kind !== "number") {
        listState = {
          kind: "number",
          start: Number(numberListMatch[1]),
          items: [],
        };
      }
      listState.items.push({ text: value, line: lineNumber });
      continue;
    }

    const bulletListMatch = /^[-*+]\s+(.*)$/u.exec(trimmed);
    if (bulletListMatch) {
      flushParagraph();
      flushBlockquote();
      const value = bulletListMatch[1];
      if (!listState || listState.kind !== "bullet") {
        listState = { kind: "bullet", start: 1, items: [] };
      }
      listState.items.push({ text: value.replace(/^\[[ xX]\]\s+/u, ""), line: lineNumber });
      continue;
    }

    if (blockquoteBuffer.length) {
      flushBlockquote();
    }

    paragraphBuffer.push({ text: trimmed, line: lineNumber });
  }

  flushParagraph();
  flushList();
  flushBlockquote();
  flushCode();

  return {
    document: { blocks, headings, source: normalized },
    warnings,
  };
}

function parseInline(text: string, line: number, warnings: MarkdownParseWarning[]): MarkdownTextRun[] {
  if (!text) return [];

  const runs: MarkdownTextRun[] = [];
  let cursor = 0;
  let plainStart = 0;

  const pushPlain = (end: number) => {
    if (end > plainStart) {
      runs.push({ type: "text", text: text.slice(plainStart, end) });
    }
    plainStart = end;
  };

  while (cursor < text.length) {
    const char = text[cursor];

    if (char === "`") {
      const closing = text.indexOf("`", cursor + 1);
      if (closing === -1) {
        cursor += 1;
        continue;
      }
      pushPlain(cursor);
      const code = text.slice(cursor + 1, closing);
      runs.push({ type: "inlineCode", code });
      cursor = closing + 1;
      plainStart = cursor;
      continue;
    }

    if (char === "[") {
      const closingBracket = text.indexOf("]", cursor + 1);
      const openParen = text.indexOf("(", closingBracket + 1);
      const closingParen = text.indexOf(")", openParen + 1);
      const hasLink = closingBracket !== -1 && openParen === closingBracket + 1 && closingParen !== -1;
      if (!hasLink) {
        cursor += 1;
        continue;
      }

      pushPlain(cursor);
      const label = text.slice(cursor + 1, closingBracket);
      const hrefRaw = text.slice(openParen + 1, closingParen).trim();
      const sanitized = sanitizeHref(hrefRaw);
      if (!sanitized) {
        warnings.push({ code: "MALFORMED_LINK", line, message: WARNING_MESSAGES.MALFORMED_LINK });
        runs.push({ type: "text", text: text.slice(cursor, closingParen + 1) });
      } else {
        runs.push({
          type: "link",
          text: label || sanitized,
          href: sanitized,
          external: /^(https?:)?\/\//iu.test(sanitized),
        });
      }
      cursor = closingParen + 1;
      plainStart = cursor;
      continue;
    }

    if (char === "*" || char === "_") {
      const emphasisMatch = findEmphasisMatch(text, cursor);
      if (emphasisMatch) {
        pushPlain(cursor);
        const inner = text.slice(cursor + emphasisMatch.length, emphasisMatch.closingIndex);
        const children = parseInline(inner, line, warnings);
        runs.push({
          type: "emphasis",
          variant: emphasisVariantFromLength(emphasisMatch.length),
          children,
        });
        cursor = emphasisMatch.closingIndex + emphasisMatch.length;
        plainStart = cursor;
        continue;
      }
    }

    cursor += 1;
  }

  pushPlain(text.length);
  return runs.filter((run) => (run.type === "text" ? run.text.length > 0 : true));
}

function sanitizeHref(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  const lower = value.toLowerCase();
  if (lower.startsWith("javascript:")) return null;
  if (lower.startsWith("data:")) return null;
  return value;
}

function slugify(text: string, slugCounts: Map<string, number>): string {
  const base = text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-") || "heading";
  const current = slugCounts.get(base) ?? 0;
  slugCounts.set(base, current + 1);
  return current === 0 ? base : `${base}-${current + 1}`;
}

function toPlainText(runs: MarkdownTextRun[]): string {
  return runs
    .map((run) => {
      switch (run.type) {
        case "text":
          return run.text;
        case "inlineCode":
          return run.code;
        case "link":
          return run.text;
        case "emphasis":
          return toPlainText(run.children);
        default:
          return "";
      }
    })
    .join("")
    .trim();
}

type EmphasisLength = 1 | 2 | 3;

type EmphasisMatch = {
  length: EmphasisLength;
  closingIndex: number;
};

function findEmphasisMatch(text: string, start: number): EmphasisMatch | null {
  const markerChar = text[start];
  if (markerChar !== "*" && markerChar !== "_") {
    return null;
  }

  let length: EmphasisLength = 1;
  while (length < 3 && text[start + length] === markerChar) {
    length = (length + 1) as EmphasisLength;
  }

  const marker = markerChar.repeat(length);
  let searchIndex = start + length;
  while (searchIndex < text.length) {
    const closingIndex = text.indexOf(marker, searchIndex);
    if (closingIndex === -1) {
      return null;
    }

    const inner = text.slice(start + length, closingIndex);
    if (!inner.trim()) {
      searchIndex = closingIndex + length;
      continue;
    }

    if (markerChar === "_" && isAlphaNumeric(text[start - 1]) && isAlphaNumeric(text[closingIndex + length])) {
      searchIndex = closingIndex + length;
      continue;
    }

    return { length, closingIndex };
  }

  return null;
}

function emphasisVariantFromLength(length: EmphasisLength): MarkdownEmphasisVariant {
  if (length === 1) return "italic";
  if (length === 2) return "bold";
  return "boldItalic";
}

function isAlphaNumeric(char?: string): boolean {
  if (!char) return false;
  return /[0-9A-Za-z]/u.test(char);
}

const TABLE_DIVIDER_CELL_PATTERN = /^:?-{3,}:?$/u;

function tryParseTableRow(line?: string): string[] | null {
  if (!line) {
    return null;
  }
  const trimmed = line.trim();
  if (!trimmed || !trimmed.startsWith("|")) {
    return null;
  }
  const pipeCount = (trimmed.match(/\|/g) ?? []).length;
  if (pipeCount < 2) {
    return null;
  }
  let content = trimmed.slice(1);
  if (content.endsWith("|")) {
    content = content.slice(0, -1);
  }
  const cells = splitTableCells(content);
  return cells;
}

function tryParseTableDivider(line: string | undefined, columnCount: number): string[] | null {
  const cells = tryParseTableRow(line);
  if (!cells) {
    return null;
  }
  const normalized = normalizeTableCells(cells, columnCount);
  const isDivider = normalized.every((cell) =>
    TABLE_DIVIDER_CELL_PATTERN.test(cell.replace(/\s+/g, "")),
  );
  return isDivider ? normalized : null;
}

function buildTableRow(
  cells: string[],
  columnCount: number,
  lineNumber: number,
  warnings: MarkdownParseWarning[],
): MarkdownTableRow {
  const normalized = normalizeTableCells(cells, columnCount);
  return {
    cells: normalized.map((cell) => ({
      content: parseInline(cell, lineNumber, warnings),
    })),
  };
}

function parseTableAlignments(
  dividerCells: string[],
  columnCount: number,
): MarkdownTableAlignment[] {
  const normalized = normalizeTableCells(dividerCells, columnCount);
  return normalized.map((cell) => alignmentFromDividerCell(cell));
}

function normalizeTableCells(cells: string[], columnCount: number): string[] {
  const normalized = cells.slice(0, columnCount).map((cell) => cell.trim());
  while (normalized.length < columnCount) {
    normalized.push("");
  }
  return normalized;
}

function alignmentFromDividerCell(cell: string): MarkdownTableAlignment {
  const trimmed = cell.replace(/\s+/g, "");
  const starts = trimmed.startsWith(":");
  const ends = trimmed.endsWith(":");
  if (starts && ends) {
    return "center";
  }
  if (starts) {
    return "left";
  }
  if (ends) {
    return "right";
  }
  return "none";
}

function splitTableCells(content: string): string[] {
  const cells: string[] = [];
  let buffer = "";
  let escaping = false;

  for (let index = 0; index < content.length; index += 1) {
    const char = content[index];
    if (char === "\\" && !escaping) {
      escaping = true;
      continue;
    }
    if (char === "|" && !escaping) {
      cells.push(buffer.trim());
      buffer = "";
      continue;
    }
    if (escaping) {
      buffer += char;
      escaping = false;
      continue;
    }
    buffer += char;
  }

  if (escaping) {
    buffer += "\\";
  }

  cells.push(buffer.trim());
  return cells;
}
